import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { PaperPlaneTilt, Package, QrCode, Bell, MapPin, CheckCircle, ArrowRight, ArrowLeft, Bluetooth, CaretDown, Scan, TruckTrailer, HandGrabbing, ShieldCheck, Clock, User, Tag, Scales, CurrencyEur, CreditCard, ArrowsLeftRight, Wallet, Lightning, Bus, Leaf, Path } from "@phosphor-icons/react";
import { QRCodeSVG } from "qrcode.react";
import axios from "axios";
import BagScanOrPick from "@/components/BagScanOrPick";
import CourierScheduler from "@/components/CourierScheduler";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_LABELS = {
  created: "Criada",
  bag_assigned: "Bolsa atribuida",
  awaiting_pickup: "A aguardar recolha",
  picked_up: "Recolhida",
  in_transit: "Em transito",
  at_delivery_point: "No ponto de entrega",
  delivered: "Entregue",
};

// BluMove branding
const BAG_TYPE_LABELS = {
  standard: "Standard",
  padded: "Almofadada",
  large: "Grande",
  fragile: "Fragil",
};

// Channel icon mapping
const CHANNEL_ICON = { express: Lightning, standard: Bus, economico: Leaf };

// ---- SENDER FLOW STEPS ----
const SENDER_STEPS = [
  { id: 1, key: "data", icon: PaperPlaneTilt },
  { id: 2, key: "channel", icon: Path },
  { id: 3, key: "payment", icon: CurrencyEur },
  { id: 4, key: "bag", icon: Package },
  { id: 5, key: "qr", icon: Scan },
  { id: 6, key: "circulation", icon: TruckTrailer },
];

// ---- RECEIVER FLOW STEPS ----
const RECEIVER_STEPS = [
  { id: 1, key: "notification", icon: Bell },
  { id: 2, key: "track", icon: MapPin },
  { id: 3, key: "arrived", icon: Package },
  { id: 4, key: "scan", icon: Scan },
  { id: 5, key: "receive", icon: CheckCircle },
];

function StepIndicator({ steps, currentStep, onStepClick, namespace = "sender_steps" }) {
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-1 mb-8 overflow-x-auto pb-2" data-testid="step-indicator">
      {steps.map((step, i) => {
        const isActive = step.id === currentStep;
        const isCompleted = step.id < currentStep;
        const label = step.key ? t(`${namespace}.${step.key}`, { defaultValue: step.title || step.key }) : step.title;
        return (
          <div key={step.id} className="flex items-center" style={{ flex: 1, minWidth: 0 }}>
            <button
              data-testid={`step-${step.id}`}
              onClick={() => onStepClick(step.id)}
              className="flex flex-col items-center gap-1 w-full"
              style={{ background: "none", border: "none", cursor: "pointer", padding: 4 }}
            >
              <div style={{
                width: 44, height: 44, borderRadius: 12,
                background: isActive ? "var(--primary)" : isCompleted ? "var(--success)" : "var(--bg)",
                border: `2px solid ${isActive ? "var(--primary)" : isCompleted ? "var(--success)" : "var(--border-color)"}`,
                display: "flex", alignItems: "center", justifyContent: "center",
                transition: "all 0.3s ease",
              }}>
                {isCompleted ? (
                  <CheckCircle size={20} weight="fill" color="white" />
                ) : (
                  <step.icon size={20} weight={isActive ? "fill" : "regular"} style={{ color: isActive ? "white" : "var(--text-muted)" }} />
                )}
              </div>
              <span style={{
                fontSize: "0.65rem", fontWeight: 600, textAlign: "center",
                color: isActive ? "var(--primary)" : isCompleted ? "var(--success)" : "var(--text-muted)",
                whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "100%",
              }}>
                {label}
              </span>
            </button>
            {i < steps.length - 1 && (
              <div style={{ width: 24, height: 2, background: isCompleted ? "var(--success)" : "var(--border-color)", flexShrink: 0, marginBottom: 16 }} />
            )}
          </div>
        );
      })}
    </div>
  );
}

function StepNavButtons({ currentStep, totalSteps, onPrev, onNext, nextLabel, nextDisabled }) {
  const { t } = useTranslation();
  return (
    <div className="flex justify-between mt-8">
      {currentStep > 1 ? (
        <button data-testid="step-prev-btn" onClick={onPrev} className="flex items-center gap-2" style={{ padding: "10px 20px", borderRadius: 9999, border: "1px solid var(--border-color)", background: "transparent", fontFamily: "Outfit", fontWeight: 600, cursor: "pointer", fontSize: "0.85rem", color: "var(--text-muted)" }}>
          <ArrowLeft size={16} /> {t("common.back")}
        </button>
      ) : <div />}
      {currentStep < totalSteps && (
        <button data-testid="step-next-btn" onClick={onNext} disabled={nextDisabled} className="flex items-center gap-2" style={{ padding: "10px 24px", borderRadius: 9999, border: "none", background: nextDisabled ? "var(--border-color)" : "var(--primary)", color: "white", fontFamily: "Outfit", fontWeight: 600, cursor: nextDisabled ? "default" : "pointer", fontSize: "0.85rem", opacity: nextDisabled ? 0.5 : 1 }}>
          {nextLabel || t("common.next")} <ArrowRight size={16} />
        </button>
      )}
    </div>
  );
}

// ---- SENDER STEP CONTENT ----
function SenderStep1({ form, setForm, points }) {
  const inputStyle = { width: "100%", padding: "12px 14px", borderRadius: 12, border: "1px solid var(--border-color)", fontFamily: "Manrope", fontSize: "0.875rem", background: "var(--bg)", outline: "none" };
  const labelStyle = { display: "block", fontSize: "0.7rem", fontWeight: 600, color: "var(--text-muted)", marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.05em" };

  return (
    <div data-testid="sender-step-1" className="animate-fade-in">
      <div className="text-center mb-8">
        <div style={{ width: 64, height: 64, borderRadius: 18, background: "rgba(45,74,62,0.08)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 12px" }}>
          <PaperPlaneTilt size={32} weight="duotone" style={{ color: "var(--primary)" }} />
        </div>
        <h3 style={{ fontFamily: "Outfit", fontWeight: 700, fontSize: "1.3rem" }}>Para quem quer enviar?</h3>
        <p style={{ color: "var(--text-muted)", fontSize: "0.85rem" }}>Preencha os dados do remetente e destinatario</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div style={{ padding: 20, borderRadius: 14, border: "1px solid var(--border-color)", background: "var(--surface)" }}>
          <h4 style={{ fontFamily: "Outfit", fontWeight: 600, fontSize: "0.9rem", marginBottom: 12, display: "flex", alignItems: "center", gap: 6 }}>
            <User size={16} weight="duotone" style={{ color: "var(--primary)" }} /> Remetente (Voce)
          </h4>
          <div className="flex flex-col gap-3">
            <div>
              <label style={labelStyle}>Nome</label>
              <input data-testid="sender-name-input" style={inputStyle} required value={form.sender_name} onChange={e => setForm({...form, sender_name: e.target.value})} placeholder="O seu nome" />
            </div>
            <div>
              <label style={labelStyle}>Morada</label>
              <input data-testid="sender-address-input" style={inputStyle} required value={form.sender_address} onChange={e => setForm({...form, sender_address: e.target.value})} placeholder="Rua Augusta 120, Lisboa" />
            </div>
          </div>
        </div>
        <div style={{ padding: 20, borderRadius: 14, border: "1px solid var(--border-color)", background: "var(--surface)" }}>
          <h4 style={{ fontFamily: "Outfit", fontWeight: 600, fontSize: "0.9rem", marginBottom: 12, display: "flex", alignItems: "center", gap: 6 }}>
            <User size={16} weight="duotone" style={{ color: "var(--accent)" }} /> Destinatario
          </h4>
          <div className="flex flex-col gap-3">
            <div>
              <label style={labelStyle}>Nome</label>
              <input data-testid="recipient-name-input" style={inputStyle} required value={form.recipient_name} onChange={e => setForm({...form, recipient_name: e.target.value})} placeholder="Nome do destinatario" />
            </div>
            <div>
              <label style={labelStyle}>Morada</label>
              <input data-testid="recipient-address-input" style={inputStyle} required value={form.recipient_address} onChange={e => setForm({...form, recipient_address: e.target.value})} placeholder="Rua Santa Catarina 200, Porto" />
            </div>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
        <div>
          <label style={labelStyle}>Ponto de recolha</label>
          <div style={{ position: "relative" }}>
            <select data-testid="pickup-point-select" required value={form.pickup_point_id} onChange={e => setForm({...form, pickup_point_id: e.target.value})} style={{ ...inputStyle, appearance: "none", paddingRight: 36 }}>
              <option value="">Onde vai depositar?</option>
              {points.map(p => <option key={p.id} value={p.id}>{p.name} - {p.city}</option>)}
            </select>
            <CaretDown size={16} style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", color: "var(--text-muted)", pointerEvents: "none" }} />
          </div>
        </div>
        <div>
          <label style={labelStyle}>Ponto de entrega</label>
          <div style={{ position: "relative" }}>
            <select data-testid="delivery-point-select" required value={form.delivery_point_id} onChange={e => setForm({...form, delivery_point_id: e.target.value})} style={{ ...inputStyle, appearance: "none", paddingRight: 36 }}>
              <option value="">Onde vai ser entregue?</option>
              {points.map(p => <option key={p.id} value={p.id}>{p.name} - {p.city}</option>)}
            </select>
            <CaretDown size={16} style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", color: "var(--text-muted)", pointerEvents: "none" }} />
          </div>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4 mt-4">
        <div>
          <label style={labelStyle}>Peso (kg)</label>
          <input data-testid="weight-input" type="number" step="0.1" min="0.1" max="10" style={inputStyle} value={form.weight_kg} onChange={e => setForm({...form, weight_kg: parseFloat(e.target.value) || 0.5})} />
        </div>
        <div>
          <label style={labelStyle}>Tipo de bolsa</label>
          <div style={{ position: "relative" }}>
            <select data-testid="bag-type-select" value={form.bag_type} onChange={e => setForm({...form, bag_type: e.target.value})} style={{ ...inputStyle, appearance: "none", paddingRight: 36 }}>
              <option value="standard">Standard (ate 3kg)</option>
              <option value="padded">Almofadada (protecao extra)</option>
              <option value="large">Grande (ate 5kg)</option>
              <option value="fragile">Fragil (acolchoada)</option>
            </select>
            <CaretDown size={16} style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", color: "var(--text-muted)", pointerEvents: "none" }} />
          </div>
        </div>
      </div>
    </div>
  );
}

function SenderChannelStep({ form, setForm, quote, loadingQuote }) {
  const { t, i18n } = useTranslation();
  const dateLocale = { pt: "pt-PT", en: "en-GB", es: "es-ES" }[i18n.language?.slice(0,2)] || "pt-PT";
  return (
    <div data-testid="sender-step-channel" className="animate-fade-in">
      <div className="text-center mb-8">
        <div style={{ width: 64, height: 64, borderRadius: 18, background: "rgba(45,74,62,0.08)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 12px" }}>
          <Path size={32} weight="duotone" style={{ color: "var(--primary)" }} />
        </div>
        <h3 style={{ fontFamily: "Outfit", fontWeight: 700, fontSize: "1.3rem" }}>{t("sender.channel_title")}</h3>
        <p style={{ color: "var(--text-muted)", fontSize: "0.85rem" }}>
          {t("sender.channel_subtitle")}
        </p>
        {quote && (
          <p style={{ color: "var(--text-muted)", fontSize: "0.75rem", marginTop: 6 }}>
            {quote.origin?.city} → {quote.destination?.city} · <strong>{quote.distance_km} km</strong>
          </p>
        )}
      </div>

      {loadingQuote && (
        <div className="text-center p-8" data-testid="quote-loading">
          <Clock size={28} style={{ color: "var(--text-muted)", margin: "0 auto" }} />
          <p style={{ color: "var(--text-muted)", marginTop: 8, fontSize: "0.85rem" }}>{t("sender.channel_loading")}</p>
        </div>
      )}

      {!loadingQuote && quote && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="channel-options">
          {quote.channels.map(ch => {
            const Icon = CHANNEL_ICON[ch.id] || Path;
            const selected = form.channel === ch.id;
            const isExpress = ch.id === "express";
            const standard = quote.channels.find(c => c.id === "standard");
            const deltaHoursVsStandard = standard ? Math.max(0, standard.duration_hours - ch.duration_hours) : 0;
            return (
              <button
                key={ch.id}
                data-testid={`channel-${ch.id}`}
                onClick={() => setForm({ ...form, channel: ch.id })}
                className="text-left card-hover"
                style={{
                  position: "relative",
                  padding: 18,
                  borderRadius: 16,
                  border: `2px solid ${selected ? ch.color : isExpress ? `${ch.color}80` : "var(--border-color)"}`,
                  background: selected ? `${ch.color}0d` : "var(--surface)",
                  cursor: "pointer",
                  transition: "all .25s",
                  boxShadow: selected ? `0 10px 30px -12px ${ch.color}66` : isExpress ? `0 6px 22px -14px ${ch.color}80` : "none",
                  transform: isExpress && !selected ? "translateY(-3px)" : "none",
                }}
              >
                {/* Most popular badge — only on Express */}
                {isExpress && (
                  <div data-testid="channel-popular-badge" style={{
                    position: "absolute", top: -10, left: 14,
                    padding: "3px 10px", borderRadius: 999,
                    background: ch.color, color: "white",
                    fontFamily: "Outfit", fontWeight: 700, fontSize: "0.6rem",
                    letterSpacing: "0.08em", textTransform: "uppercase",
                    boxShadow: `0 4px 14px -4px ${ch.color}aa`,
                    display: "inline-flex", alignItems: "center", gap: 4,
                  }}>
                    <Lightning size={10} weight="fill" /> {t("sender.channel_popular_badge")}
                  </div>
                )}
                {selected && (
                  <div style={{ position: "absolute", top: 12, right: 12, width: 22, height: 22, borderRadius: 11, background: ch.color, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <CheckCircle size={14} weight="fill" color="white" />
                  </div>
                )}
                <div style={{ width: 48, height: 48, borderRadius: 14, background: `${ch.color}1a`, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 12 }}>
                  <Icon size={26} weight="duotone" style={{ color: ch.color }} />
                </div>
                <div style={{ fontFamily: "Outfit", fontWeight: 700, fontSize: "1.05rem" }}>{t(`channels.${ch.id}_label`, { defaultValue: ch.label })}</div>
                <p style={{ color: "var(--text-muted)", fontSize: "0.75rem", marginTop: 2, marginBottom: 12 }}>{t(`channels.${ch.id}_tagline`, { defaultValue: ch.tagline })}</p>
                <div className="flex items-baseline gap-1" style={{ marginBottom: 8 }}>
                  <span style={{ fontFamily: "Outfit", fontWeight: 800, fontSize: "1.6rem", color: ch.color }}>{ch.price_eur.toFixed(2)}</span>
                  <span style={{ fontSize: "0.75rem", color: "var(--text-muted)" }}>EUR</span>
                </div>
                <div className="flex items-center gap-2" style={{ fontSize: "0.75rem", color: "var(--text-muted)", marginBottom: 10 }}>
                  <Clock size={14} /> {t("sender.channel_eta", { hours: ch.duration_hours, date: new Date(ch.arrival).toLocaleString(dateLocale, { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) })}
                </div>
                {/* Delta time vs Standard — visible only on Express */}
                {isExpress && deltaHoursVsStandard > 0 && (
                  <div data-testid="channel-time-saved" style={{
                    margin: "0 0 10px 0", padding: "6px 10px", borderRadius: 8,
                    background: `${ch.color}14`, border: `1px solid ${ch.color}33`,
                    fontSize: "0.7rem", color: ch.color, fontWeight: 600,
                    display: "flex", alignItems: "center", gap: 6,
                  }}>
                    <Lightning size={12} weight="fill" /> {t("sender.channel_time_saved", { hours: deltaHoursVsStandard })}
                  </div>
                )}
                <div style={{ borderTop: "1px dashed var(--border-color)", paddingTop: 8 }}>
                  {ch.features.map((f, i) => (
                    <div key={i} className="flex items-center gap-2" style={{ fontSize: "0.72rem", color: "var(--text-main)", marginBottom: 4 }}>
                      <CheckCircle size={12} weight="fill" style={{ color: ch.color, flexShrink: 0 }} /> {f}
                    </div>
                  ))}
                </div>
                <div style={{ marginTop: 10, fontSize: "0.7rem", color: "var(--text-muted)" }}>
                  {t("sender.channel_co2")}: <strong style={{ color: ch.color }}>{ch.co2_saved_kg} kg</strong>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function SenderStep2({ form, setForm, shippingCost }) {
  const PAYMENT_MODES = [
    { id: "sender_pays", label: "Eu pago tudo", desc: "O remetente paga o envio", icon: User },
    { id: "receiver_pays", label: "Destinatario paga", desc: "Quem recebe paga o envio", icon: Wallet },
    { id: "split", label: "Dividir custos", desc: "Custos divididos entre ambos", icon: ArrowsLeftRight },
  ];

  const senderAmount = form.payment_mode === "sender_pays" ? shippingCost : form.payment_mode === "split" ? Math.round(shippingCost / 2 * 100) / 100 : 0;
  const receiverAmount = form.payment_mode === "receiver_pays" ? shippingCost : form.payment_mode === "split" ? Math.round((shippingCost - Math.round(shippingCost / 2 * 100) / 100) * 100) / 100 : 0;
  const productValue = form.pay_on_delivery ? (parseFloat(form.product_value) || 0) : 0;
  const receiverTotal = receiverAmount + productValue;

  return (
    <div data-testid="sender-step-2" className="animate-fade-in">
      <div className="text-center mb-8">
        <div style={{ width: 64, height: 64, borderRadius: 18, background: "rgba(37,99,235,0.08)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 12px" }}>
          <CurrencyEur size={32} weight="duotone" style={{ color: "#2563eb" }} />
        </div>
        <h3 style={{ fontFamily: "Outfit", fontWeight: 700, fontSize: "1.3rem" }}>Como pretende pagar?</h3>
        <p style={{ color: "var(--text-muted)", fontSize: "0.85rem" }}>Defina o valor do produto e quem paga o envio</p>
      </div>

      {/* Shipping cost display */}
      <div className="bento-item p-5 mb-6" style={{ background: "var(--bg)" }}>
        <div className="flex items-center justify-between">
          <div>
            <span style={{ fontWeight: 600, fontSize: "0.9rem" }}>Custo de envio</span>
            <p style={{ color: "var(--text-muted)", fontSize: "0.7rem", marginTop: 2 }}>
              Canal escolhido: <strong style={{ textTransform: "capitalize" }}>{form.channel}</strong> · {form.weight_kg}kg · {BAG_TYPE_LABELS[form.bag_type] || form.bag_type}
            </p>
          </div>
          <span style={{ fontFamily: "Outfit", fontWeight: 700, fontSize: "1.3rem", color: "var(--primary)" }}>{shippingCost.toFixed(2)} EUR</span>
        </div>
      </div>

      {/* Pay on delivery toggle */}
      <div className="bento-item p-5 mb-6 cursor-pointer" onClick={() => setForm({...form, pay_on_delivery: !form.pay_on_delivery})} style={{ border: form.pay_on_delivery ? "2px solid var(--accent)" : "1px solid var(--border-color)" }}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div style={{ width: 40, height: 40, borderRadius: 10, background: form.pay_on_delivery ? "rgba(217,108,74,0.1)" : "var(--bg)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <CreditCard size={20} weight="duotone" style={{ color: form.pay_on_delivery ? "var(--accent)" : "var(--text-muted)" }} />
            </div>
            <div>
              <span style={{ fontFamily: "Outfit", fontWeight: 600, fontSize: "0.95rem" }}>Envio a pagar no destino (COD)</span>
              <p style={{ color: "var(--text-muted)", fontSize: "0.75rem" }}>O destinatario paga o valor do produto antes da entrega</p>
            </div>
          </div>
          <div data-testid="cod-toggle" style={{ width: 44, height: 24, borderRadius: 12, background: form.pay_on_delivery ? "var(--accent)" : "var(--border-color)", padding: 2, cursor: "pointer", transition: "background 0.2s" }}>
            <div style={{ width: 20, height: 20, borderRadius: 10, background: "white", transform: form.pay_on_delivery ? "translateX(20px)" : "translateX(0)", transition: "transform 0.2s", boxShadow: "0 1px 3px rgba(0,0,0,0.2)" }} />
          </div>
        </div>
      </div>

      {/* Product value (COD) */}
      {form.pay_on_delivery && (
        <div className="mb-6 animate-fade-in">
          <label style={{ display: "block", fontSize: "0.7rem", fontWeight: 600, color: "var(--text-muted)", marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.05em" }}>Valor do produto (EUR)</label>
          <input
            data-testid="product-value-input"
            type="number" step="0.01" min="0"
            value={form.product_value}
            onChange={e => setForm({...form, product_value: parseFloat(e.target.value) || 0})}
            style={{ width: "100%", padding: "12px 14px", borderRadius: 12, border: "1px solid var(--border-color)", fontFamily: "Manrope", fontSize: "0.9rem", background: "var(--bg)", outline: "none" }}
            placeholder="Ex: 25.00"
          />
          <p style={{ color: "var(--text-muted)", fontSize: "0.7rem", marginTop: 4 }}>Este valor sera cobrado ao destinatario e libertado apos a entrega</p>
        </div>
      )}

      {/* Who pays shipping */}
      <div className="mb-6">
        <label style={{ display: "block", fontSize: "0.7rem", fontWeight: 600, color: "var(--text-muted)", marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.05em" }}>Quem paga o envio?</label>
        <div className="grid grid-cols-3 gap-3">
          {PAYMENT_MODES.map(mode => (
            <button
              key={mode.id}
              data-testid={`payment-mode-${mode.id}`}
              onClick={() => setForm({...form, payment_mode: mode.id})}
              className="text-center card-hover"
              style={{
                padding: "14px 10px", borderRadius: 14,
                border: `2px solid ${form.payment_mode === mode.id ? "var(--primary)" : "var(--border-color)"}`,
                background: form.payment_mode === mode.id ? "rgba(45,74,62,0.04)" : "var(--surface)",
                cursor: "pointer",
              }}
            >
              <mode.icon size={22} weight={form.payment_mode === mode.id ? "fill" : "regular"} style={{ color: form.payment_mode === mode.id ? "var(--primary)" : "var(--text-muted)", margin: "0 auto 6px" }} />
              <span style={{ fontFamily: "Outfit", fontWeight: 600, fontSize: "0.8rem", display: "block", color: form.payment_mode === mode.id ? "var(--primary)" : "var(--text-main)" }}>{mode.label}</span>
              <span style={{ fontSize: "0.65rem", color: "var(--text-muted)" }}>{mode.desc}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Summary */}
      <div className="bento-item p-5" style={{ background: "var(--primary)", color: "white", borderColor: "var(--primary)" }}>
        <h4 style={{ fontFamily: "Outfit", fontWeight: 600, fontSize: "0.9rem", marginBottom: 10, color: "rgba(255,255,255,0.7)" }}>Resumo do pagamento</h4>
        <div className="flex flex-col gap-2">
          <div className="flex justify-between" style={{ fontSize: "0.85rem" }}>
            <span style={{ color: "rgba(255,255,255,0.7)" }}>Remetente paga</span>
            <span style={{ fontWeight: 700 }}>{senderAmount.toFixed(2)} EUR</span>
          </div>
          <div className="flex justify-between" style={{ fontSize: "0.85rem" }}>
            <span style={{ color: "rgba(255,255,255,0.7)" }}>Destinatario paga (envio)</span>
            <span style={{ fontWeight: 700 }}>{receiverAmount.toFixed(2)} EUR</span>
          </div>
          {form.pay_on_delivery && productValue > 0 && (
            <div className="flex justify-between" style={{ fontSize: "0.85rem" }}>
              <span style={{ color: "rgba(255,255,255,0.7)" }}>Valor do produto (COD)</span>
              <span style={{ fontWeight: 700 }}>{productValue.toFixed(2)} EUR</span>
            </div>
          )}
          <div style={{ borderTop: "1px solid rgba(255,255,255,0.2)", paddingTop: 8, marginTop: 4 }} className="flex justify-between">
            <span style={{ fontWeight: 700 }}>Total</span>
            <span style={{ fontFamily: "Outfit", fontWeight: 700, fontSize: "1.1rem" }}>{(senderAmount + receiverTotal).toFixed(2)} EUR</span>
          </div>
        </div>
        {form.pay_on_delivery && (
          <div style={{ marginTop: 10, padding: "8px 12px", borderRadius: 8, background: "rgba(255,255,255,0.1)", fontSize: "0.75rem", color: "rgba(255,255,255,0.8)" }}>
            O pagamento do destinatario fica retido ate a confirmacao da entrega
          </div>
        )}
      </div>
    </div>
  );
}

function SenderStep3({ shipmentResult }) {
  if (!shipmentResult) return <div className="text-center p-8"><Clock size={32} style={{ color: "var(--text-muted)", margin: "0 auto" }} /><p style={{ color: "var(--text-muted)", marginTop: 8 }}>A criar envio...</p></div>;
  return (
    <div data-testid="sender-step-3" className="animate-fade-in text-center">
      <div style={{ width: 80, height: 80, borderRadius: 20, background: "rgba(62,102,86,0.1)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
        <Package size={40} weight="duotone" style={{ color: "var(--success)" }} />
      </div>
      <h3 style={{ fontFamily: "Outfit", fontWeight: 700, fontSize: "1.3rem", marginBottom: 4 }}>Bolsa atribuida!</h3>
      <p style={{ color: "var(--text-muted)", fontSize: "0.9rem", marginBottom: 24 }}>O sistema selecionou a bolsa ideal. {shipmentResult.pay_on_delivery ? "Pagamento COD ativado." : ""}</p>
      <div style={{ maxWidth: 400, margin: "0 auto", padding: 24, borderRadius: 16, background: "var(--bg)", border: "1px solid var(--border-color)" }}>
        <div className="flex items-center justify-center gap-3 mb-4">
          <Bluetooth size={20} weight="duotone" style={{ color: "var(--primary)" }} />
          <span className="bag-code" style={{ fontSize: "1.5rem" }}>{shipmentResult.bag_code}</span>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div style={{ padding: 10, borderRadius: 10, background: "var(--surface)" }}>
            <span style={{ fontSize: "0.65rem", color: "var(--text-muted)", textTransform: "uppercase", fontWeight: 600 }}>Tipo</span>
            <p style={{ fontWeight: 600, fontSize: "0.85rem" }}>{BAG_TYPE_LABELS[shipmentResult.bag_type] || shipmentResult.bag_type}</p>
          </div>
          <div style={{ padding: 10, borderRadius: 10, background: "var(--surface)" }}>
            <span style={{ fontSize: "0.65rem", color: "var(--text-muted)", textTransform: "uppercase", fontWeight: 600 }}>Envio</span>
            <p style={{ fontWeight: 600, fontSize: "0.85rem" }}>{shipmentResult.shipping_cost?.toFixed(2) || "0.00"} EUR</p>
          </div>
        </div>
        {shipmentResult.pay_on_delivery && (
          <div className="mt-3" style={{ padding: "8px 12px", borderRadius: 8, background: "rgba(217,108,74,0.06)", fontSize: "0.8rem" }}>
            <span style={{ color: "var(--accent)", fontWeight: 600 }}>COD ativo</span>
            <span style={{ color: "var(--text-muted)" }}> - Destinatario pagara {shipmentResult.receiver_amount?.toFixed(2)} EUR</span>
          </div>
        )}
      </div>
    </div>
  );
}

function SenderStep4({ shipmentResult }) {
  return (
    <div data-testid="sender-step-4" className="animate-fade-in text-center">
      <div style={{ width: 80, height: 80, borderRadius: 20, background: "rgba(37,99,235,0.1)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
        <QrCode size={40} weight="duotone" style={{ color: "#2563eb" }} />
      </div>
      <h3 style={{ fontFamily: "Outfit", fontWeight: 700, fontSize: "1.3rem", marginBottom: 4 }}>Confirme o envio com QR Code</h3>
      <p style={{ color: "var(--text-muted)", fontSize: "0.9rem", marginBottom: 24 }}>Leia o QR Code da bolsa para confirmar o codigo de rastreio</p>
      {shipmentResult && (
        <div style={{ maxWidth: 360, margin: "0 auto" }}>
          <div className="bento-item p-6">
            <div style={{ background: "white", padding: 16, borderRadius: 12, display: "inline-block", marginBottom: 16 }}>
              <QRCodeSVG
                value={`BLUMOVE:${shipmentResult.tracking_code}:${shipmentResult.bag_code}`}
                size={160}
                bgColor="white"
                fgColor="#2D4A3E"
                level="M"
              />
            </div>
            <div className="mono" style={{ fontSize: "1.2rem", fontWeight: 700, color: "var(--primary)", marginBottom: 4 }}>{shipmentResult.tracking_code}</div>
            <div className="flex items-center justify-center gap-2 mb-3" style={{ fontSize: "0.8rem", color: "var(--text-muted)" }}>
              <Bluetooth size={14} /> Bolsa {shipmentResult.bag_code}
            </div>
            <div style={{ padding: "8px 14px", borderRadius: 8, background: "rgba(62,102,86,0.06)", fontSize: "0.8rem", display: "inline-flex", alignItems: "center", gap: 6 }}>
              <CheckCircle size={16} weight="fill" style={{ color: "var(--success)" }} />
              <span style={{ color: "var(--success)", fontWeight: 600 }}>QR Code confirmado</span>
            </div>
          </div>
          <p style={{ color: "var(--text-muted)", fontSize: "0.75rem", marginTop: 12 }}>Guarde o codigo de rastreio. Pode acompanhar o envio a qualquer momento.</p>
        </div>
      )}
    </div>
  );
}

function SenderStep5({ shipmentResult }) {
  const timeline = shipmentResult?.timeline || [];
  return (
    <div data-testid="sender-step-5" className="animate-fade-in text-center">
      <div style={{ width: 80, height: 80, borderRadius: 20, background: "rgba(62,102,86,0.1)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
        <TruckTrailer size={40} weight="duotone" style={{ color: "var(--success)" }} />
      </div>
      <h3 style={{ fontFamily: "Outfit", fontWeight: 700, fontSize: "1.3rem", marginBottom: 4 }}>Encomenda no circuito!</h3>
      <p style={{ color: "var(--text-muted)", fontSize: "0.9rem", marginBottom: 24 }}>A bolsa entrou na rede de distribuicao BluMove. Acompanhe o progresso.</p>
      {shipmentResult && (
        <div style={{ maxWidth: 500, margin: "0 auto", textAlign: "left" }}>
          <div className="bento-item p-6 mb-4">
            <div className="flex items-center justify-between mb-4">
              <span className="mono" style={{ fontSize: "1rem", fontWeight: 700, color: "var(--primary)" }}>{shipmentResult.tracking_code}</span>
              <span className="status-awaiting_pickup" style={{ padding: "4px 12px", borderRadius: 9999, fontSize: "0.75rem", fontWeight: 600 }}>A aguardar recolha</span>
            </div>
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div style={{ padding: 10, borderRadius: 10, background: "var(--bg)" }}>
                <span style={{ fontSize: "0.65rem", color: "var(--text-muted)", textTransform: "uppercase", fontWeight: 600, display: "block" }}>Recolha</span>
                <p style={{ fontWeight: 600, fontSize: "0.8rem" }}>{shipmentResult.pickup_point}</p>
              </div>
              <div style={{ padding: 10, borderRadius: 10, background: "var(--bg)" }}>
                <span style={{ fontSize: "0.65rem", color: "var(--text-muted)", textTransform: "uppercase", fontWeight: 600, display: "block" }}>Entrega</span>
                <p style={{ fontWeight: 600, fontSize: "0.8rem" }}>{shipmentResult.delivery_point}</p>
              </div>
            </div>
            {/* Mini timeline */}
            <div className="flex flex-col gap-3">
              {timeline.map((step, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div style={{ width: 24, height: 24, borderRadius: "50%", background: i === timeline.length - 1 ? "var(--primary)" : "var(--success)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    {i === timeline.length - 1 ? <div style={{ width: 8, height: 8, borderRadius: "50%", background: "white" }} /> : <CheckCircle size={14} weight="fill" color="white" />}
                  </div>
                  <div>
                    <span style={{ fontSize: "0.8rem", fontWeight: 600 }}>{step.description}</span>
                    <span style={{ fontSize: "0.65rem", color: "var(--text-muted)", marginLeft: 8 }}>
                      {new Date(step.timestamp).toLocaleString("pt-PT", { hour: "2-digit", minute: "2-digit" })}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div style={{ padding: "12px 16px", borderRadius: 12, background: "rgba(37,99,235,0.06)", border: "1px solid rgba(37,99,235,0.1)", display: "flex", alignItems: "center", gap: 8 }}>
            <Bell size={18} weight="duotone" style={{ color: "#2563eb" }} />
            <span style={{ fontSize: "0.8rem", color: "#2563eb", fontWeight: 500 }}>Recebera notificacoes sobre o progresso da encomenda</span>
          </div>
        </div>
      )}
    </div>
  );
}

// ---- RECEIVER FLOW ----
function ReceiverStep1({ selectedShipment }) {
  return (
    <div data-testid="receiver-step-1" className="animate-fade-in text-center">
      <div style={{ width: 80, height: 80, borderRadius: 20, background: "rgba(37,99,235,0.1)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
        <Bell size={40} weight="duotone" style={{ color: "#2563eb" }} />
      </div>
      <h3 style={{ fontFamily: "Outfit", fontWeight: 700, fontSize: "1.3rem", marginBottom: 4 }}>Tem uma encomenda a caminho!</h3>
      <p style={{ color: "var(--text-muted)", fontSize: "0.9rem", marginBottom: 24 }}>Recebeu uma notificacao de que alguem lhe enviou uma encomenda via BluMove</p>
      {selectedShipment && (
        <div style={{ maxWidth: 480, margin: "0 auto" }}>
          <div className="bento-item p-6">
            <div style={{ padding: "10px 14px", borderRadius: 10, background: "rgba(37,99,235,0.06)", marginBottom: 16, display: "flex", alignItems: "center", gap: 8 }}>
              <Bell size={18} weight="fill" style={{ color: "#2563eb" }} />
              <span style={{ fontSize: "0.85rem", fontWeight: 500 }}>Nova encomenda de <strong>{selectedShipment.sender_name}</strong></span>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div style={{ padding: 10, borderRadius: 10, background: "var(--bg)" }}>
                <span style={{ fontSize: "0.65rem", color: "var(--text-muted)", textTransform: "uppercase", fontWeight: 600, display: "block" }}>De</span>
                <p style={{ fontWeight: 600, fontSize: "0.85rem" }}>{selectedShipment.sender_name}</p>
              </div>
              <div style={{ padding: 10, borderRadius: 10, background: "var(--bg)" }}>
                <span style={{ fontSize: "0.65rem", color: "var(--text-muted)", textTransform: "uppercase", fontWeight: 600, display: "block" }}>Rastreio</span>
                <p className="mono" style={{ fontWeight: 600, fontSize: "0.8rem", color: "var(--primary)" }}>{selectedShipment.tracking_code}</p>
              </div>
            </div>
          </div>
          {/* Optional: schedule home delivery with courier (pay-on-delivery) */}
          <CourierScheduler
            trackingCode={selectedShipment.tracking_code}
            defaultAddress={selectedShipment.recipient_address || ""}
          />
        </div>
      )}
    </div>
  );
}

function ReceiverStep2({ selectedShipment }) {
  const timeline = selectedShipment?.timeline || [];
  return (
    <div data-testid="receiver-step-2" className="animate-fade-in text-center">
      <div style={{ width: 80, height: 80, borderRadius: 20, background: "rgba(45,74,62,0.08)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
        <MapPin size={40} weight="duotone" style={{ color: "var(--primary)" }} />
      </div>
      <h3 style={{ fontFamily: "Outfit", fontWeight: 700, fontSize: "1.3rem", marginBottom: 4 }}>Acompanhe em tempo real</h3>
      <p style={{ color: "var(--text-muted)", fontSize: "0.9rem", marginBottom: 24 }}>Veja onde esta a sua encomenda no circuito BluMove</p>
      {selectedShipment && (
        <div style={{ maxWidth: 500, margin: "0 auto", textAlign: "left" }}>
          <div className="bento-item p-6">
            <div className="flex flex-col gap-3">
              {timeline.map((step, i) => (
                <div key={i} className="flex items-start gap-3" style={{ position: "relative", paddingBottom: i < timeline.length - 1 ? 8 : 0 }}>
                  <div style={{ width: 28, height: 28, borderRadius: "50%", background: i === timeline.length - 1 ? "var(--primary)" : "var(--success)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    {i === timeline.length - 1 ? <div style={{ width: 8, height: 8, borderRadius: "50%", background: "white" }} /> : <CheckCircle size={14} weight="fill" color="white" />}
                  </div>
                  <div>
                    <p style={{ fontSize: "0.85rem", fontWeight: 600 }}>{step.description}</p>
                    <div className="flex items-center gap-2" style={{ fontSize: "0.7rem", color: "var(--text-muted)" }}>
                      <span>{step.location}</span>
                      <span>{new Date(step.timestamp).toLocaleString("pt-PT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ReceiverStep3({ selectedShipment }) {
  return (
    <div data-testid="receiver-step-3" className="animate-fade-in text-center">
      <div style={{ width: 80, height: 80, borderRadius: 20, background: "rgba(232,163,89,0.1)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
        <Package size={40} weight="duotone" style={{ color: "var(--warning)" }} />
      </div>
      <h3 style={{ fontFamily: "Outfit", fontWeight: 700, fontSize: "1.3rem", marginBottom: 4 }}>Encomenda no ponto de entrega!</h3>
      <p style={{ color: "var(--text-muted)", fontSize: "0.9rem", marginBottom: 24 }}>A sua encomenda chegou e esta pronta para recolha</p>
      {selectedShipment && (
        <div style={{ maxWidth: 400, margin: "0 auto" }}>
          <div className="bento-item p-6">
            <MapPin size={24} weight="duotone" style={{ color: "var(--primary)", margin: "0 auto 8px" }} />
            <h4 style={{ fontFamily: "Outfit", fontWeight: 700, fontSize: "1.1rem" }}>{selectedShipment.delivery_point}</h4>
            <p style={{ color: "var(--text-muted)", fontSize: "0.8rem", marginTop: 4 }}>Dirija-se a este ponto para recolher a sua encomenda</p>
            <div className="mt-4" style={{ padding: "10px 14px", borderRadius: 10, background: "rgba(232,163,89,0.08)", fontSize: "0.8rem", color: "var(--warning)", fontWeight: 600, display: "flex", alignItems: "center", gap: 6 }}>
              <Clock size={16} /> A aguardar a sua recolha
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ReceiverStep4({ selectedShipment }) {
  return (
    <div data-testid="receiver-step-4" className="animate-fade-in text-center">
      <div style={{ width: 80, height: 80, borderRadius: 20, background: "rgba(37,99,235,0.1)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
        <Scan size={40} weight="duotone" style={{ color: "#2563eb" }} />
      </div>
      <h3 style={{ fontFamily: "Outfit", fontWeight: 700, fontSize: "1.3rem", marginBottom: 4 }}>Leia o QR Code para confirmar</h3>
      <p style={{ color: "var(--text-muted)", fontSize: "0.9rem", marginBottom: 24 }}>Leia o QR Code na bolsa para confirmar a sua identidade e desbloquear o trinco</p>
      {selectedShipment && (
        <div style={{ maxWidth: 360, margin: "0 auto" }}>
          <div className="bento-item p-6">
            <div style={{ background: "white", padding: 16, borderRadius: 12, display: "inline-block", marginBottom: 16 }}>
              <QRCodeSVG value={`BLUMOVE:${selectedShipment.tracking_code}:${selectedShipment.bag_code}`} size={140} bgColor="white" fgColor="#2D4A3E" level="M" />
            </div>
            <div style={{ padding: "10px 14px", borderRadius: 10, background: "rgba(62,102,86,0.06)", display: "flex", alignItems: "center", gap: 8, justifyContent: "center" }}>
              <ShieldCheck size={18} weight="duotone" style={{ color: "var(--success)" }} />
              <span style={{ fontSize: "0.85rem", fontWeight: 600, color: "var(--success)" }}>Identidade confirmada - Trinco desbloqueado</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ReceiverStep5({ selectedShipment }) {
  return (
    <div data-testid="receiver-step-5" className="animate-fade-in text-center">
      <div style={{ width: 80, height: 80, borderRadius: 20, background: "rgba(62,102,86,0.15)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
        <CheckCircle size={40} weight="fill" style={{ color: "var(--success)" }} />
      </div>
      <h3 style={{ fontFamily: "Outfit", fontWeight: 700, fontSize: "1.3rem", marginBottom: 4 }}>Encomenda recebida!</h3>
      <p style={{ color: "var(--text-muted)", fontSize: "0.9rem", marginBottom: 24 }}>Retire o produto e devolva a bolsa. Ela volta automaticamente ao circuito.</p>
      <div style={{ maxWidth: 500, margin: "0 auto" }}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            { step: "1", text: "Abra a bolsa", desc: "Trinco Bluetooth desbloqueado", icon: "🔓" },
            { step: "2", text: "Retire o produto", desc: "Retire o seu produto com cuidado", icon: "🎁" },
            { step: "3", text: "Devolva a bolsa", desc: "Deixe no ponto. Volta ao circuito!", icon: "♻️" },
          ].map((s, i) => (
            <div key={i} className="bento-item p-5 text-center">
              <div style={{ fontSize: "2rem", marginBottom: 8 }}>{s.icon}</div>
              <div style={{ width: 28, height: 28, borderRadius: "50%", background: "var(--success)", color: "white", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "Outfit", fontWeight: 700, fontSize: "0.8rem", margin: "0 auto 8px" }}>{s.step}</div>
              <h4 style={{ fontFamily: "Outfit", fontWeight: 600, fontSize: "0.9rem" }}>{s.text}</h4>
              <p style={{ color: "var(--text-muted)", fontSize: "0.75rem" }}>{s.desc}</p>
            </div>
          ))}
        </div>
        <div className="mt-4" style={{ padding: "14px 18px", borderRadius: 14, background: "var(--primary)", color: "white", display: "flex", alignItems: "center", gap: 10, justifyContent: "center" }}>
          <CheckCircle size={20} weight="fill" />
          <span style={{ fontWeight: 600 }}>Entrega concluida com sucesso!</span>
        </div>
      </div>
    </div>
  );
}

// ---- MAIN COMPONENT ----
export default function UserDemo({ shipments, points, bags, createShipment, loading }) {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState("sender");
  const [senderStep, setSenderStep] = useState(1);
  const [receiverStep, setReceiverStep] = useState(1);
  const [shipmentResult, setShipmentResult] = useState(null);
  const [selectedShipment, setSelectedShipment] = useState(null);
  const [form, setForm] = useState({
    sender_name: "", sender_address: "", recipient_name: "", recipient_address: "",
    pickup_point_id: "", delivery_point_id: "", weight_kg: 0.5, bag_type: "standard",
    payment_mode: "sender_pays", product_value: 0, pay_on_delivery: false,
    channel: "standard",
  });
  const [shippingCost, setShippingCost] = useState(3.0);
  const [quote, setQuote] = useState(null);
  const [loadingQuote, setLoadingQuote] = useState(false);

  // Fetch quote (3 channels) when route or weight changes
  const fetchQuote = useCallback(async () => {
    if (!form.pickup_point_id || !form.delivery_point_id) return;
    setLoadingQuote(true);
    try {
      const res = await axios.post(`${API}/shipments/quote`, {
        pickup_point_id: form.pickup_point_id,
        delivery_point_id: form.delivery_point_id,
        weight_kg: form.weight_kg,
        bag_type: form.bag_type,
      });
      setQuote(res.data);
    } catch (e) {
      console.error("Quote error:", e);
    } finally {
      setLoadingQuote(false);
    }
  }, [form.pickup_point_id, form.delivery_point_id, form.weight_kg, form.bag_type]);

  // When entering step 2 OR when route/weight changes while on step 2+, refresh quote
  useEffect(() => {
    if (senderStep >= 2 && form.pickup_point_id && form.delivery_point_id) {
      fetchQuote();
    }
  }, [senderStep, fetchQuote, form.pickup_point_id, form.delivery_point_id]);

  // Update shippingCost from selected channel quote
  useEffect(() => {
    if (quote && quote.channels) {
      const ch = quote.channels.find(c => c.id === form.channel);
      if (ch) setShippingCost(ch.price_eur);
    }
  }, [quote, form.channel]);

  const canProceedSender = () => {
    if (senderStep === 1) return form.sender_name && form.recipient_name && form.pickup_point_id && form.delivery_point_id;
    if (senderStep === 2) return !!quote && !!form.channel; // channel step
    if (senderStep === 3) return true; // payment config step
    return true;
  };

  const handleSenderNext = async () => {
    if (senderStep === 3) {
      // Create shipment with payment on step 3 → 4
      try {
        const res = await axios.post(`${API}/payments/shipments/with-payment`, {
          sender_name: form.sender_name,
          sender_address: form.sender_address,
          recipient_name: form.recipient_name,
          recipient_address: form.recipient_address,
          pickup_point_id: form.pickup_point_id,
          delivery_point_id: form.delivery_point_id,
          weight_kg: form.weight_kg,
          bag_type: form.bag_type,
          payment_mode: form.payment_mode,
          product_value: form.pay_on_delivery ? (parseFloat(form.product_value) || 0) : 0,
          pay_on_delivery: form.pay_on_delivery,
          channel: form.channel,
        });
        if (res.data && !res.data.error) {
          setShipmentResult(res.data);
          setSenderStep(4);
        }
      } catch (e) { console.error(e); }
    } else {
      setSenderStep(Math.min(senderStep + 1, 6));
    }
  };

  // For receiver, select first delivered/in_transit shipment
  const receiverShipments = shipments.filter(s => ["in_transit", "delivered", "at_delivery_point"].includes(s.status));

  return (
    <div data-testid="user-demo" className="pt-20 px-6 pb-12 max-w-4xl mx-auto">
      {/* Tab selector */}
      <div className="flex justify-center gap-2 mb-8" data-testid="user-tabs">
        {[
          { id: "sender", label: t("sender.tab_send"), icon: PaperPlaneTilt, desc: t("sender.tab_send_desc") },
          { id: "receiver", label: t("sender.tab_receive"), icon: Package, desc: t("sender.tab_receive_desc") },
        ].map(tab => (
          <button
            key={tab.id}
            data-testid={`tab-${tab.id}`}
            onClick={() => setActiveTab(tab.id)}
            className="flex items-center gap-3 card-hover"
            style={{
              padding: "16px 28px", borderRadius: 16,
              border: `2px solid ${activeTab === tab.id ? "var(--primary)" : "var(--border-color)"}`,
              background: activeTab === tab.id ? "rgba(45,74,62,0.04)" : "var(--surface)",
              cursor: "pointer", fontFamily: "Manrope", transition: "all 0.3s",
            }}
          >
            <div style={{ width: 44, height: 44, borderRadius: 12, background: activeTab === tab.id ? "var(--primary)" : "var(--bg)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <tab.icon size={22} weight={activeTab === tab.id ? "fill" : "regular"} style={{ color: activeTab === tab.id ? "white" : "var(--text-muted)" }} />
            </div>
            <div style={{ textAlign: "left" }}>
              <span style={{ fontFamily: "Outfit", fontWeight: 700, fontSize: "1rem", color: activeTab === tab.id ? "var(--primary)" : "var(--text-main)", display: "block" }}>{tab.label}</span>
              <span style={{ fontSize: "0.75rem", color: "var(--text-muted)" }}>{tab.desc}</span>
            </div>
          </button>
        ))}
      </div>

      {/* SENDER FLOW */}
      {activeTab === "sender" && (
        <div data-testid="sender-flow">
          <StepIndicator steps={SENDER_STEPS} currentStep={senderStep} onStepClick={id => { if (id <= senderStep || (shipmentResult && id <= 6)) setSenderStep(id); }} />
          <div className="bento-item p-8">
            {senderStep === 1 && <SenderStep1 form={form} setForm={setForm} points={points} />}
            {senderStep === 2 && <SenderChannelStep form={form} setForm={setForm} quote={quote} loadingQuote={loadingQuote} />}
            {senderStep === 3 && <SenderStep2 form={form} setForm={setForm} shippingCost={shippingCost} />}
            {senderStep === 4 && <SenderStep3 shipmentResult={shipmentResult} />}
            {senderStep === 5 && (
              <BagScanOrPick
                shipment={shipmentResult}
                onSealed={(updated) => {
                  setShipmentResult({ ...shipmentResult, ...updated, status: "awaiting_pickup" });
                  setSenderStep(6);
                }}
              />
            )}
            {senderStep === 6 && <SenderStep5 shipmentResult={shipmentResult} />}
            <StepNavButtons
              currentStep={senderStep} totalSteps={6}
              onPrev={() => setSenderStep(Math.max(1, senderStep - 1))}
              onNext={handleSenderNext}
              nextLabel={senderStep === 1 ? t("sender.btn_view_channels") : senderStep === 2 ? t("sender.btn_continue_payment") : senderStep === 3 ? t("sender.btn_create") : t("common.next")}
              nextDisabled={!canProceedSender()}
            />
          </div>
        </div>
      )}

      {/* RECEIVER FLOW */}
      {activeTab === "receiver" && (
        <div data-testid="receiver-flow">
          {!selectedShipment ? (
            <div className="bento-item p-8 text-center animate-fade-in">
              <Package size={48} weight="duotone" style={{ color: "var(--border-color)", margin: "0 auto 12px" }} />
              <h3 style={{ fontFamily: "Outfit", fontWeight: 600, marginBottom: 8 }}>Selecione uma encomenda para simular</h3>
              <p style={{ color: "var(--text-muted)", fontSize: "0.85rem", marginBottom: 20 }}>Escolha uma encomenda existente para ver o fluxo do destinatario</p>
              <div className="flex flex-col gap-3" style={{ maxWidth: 500, margin: "0 auto" }}>
                {receiverShipments.length > 0 ? receiverShipments.map(s => (
                  <button
                    key={s.id}
                    data-testid={`receiver-shipment-${s.tracking_code}`}
                    onClick={() => { setSelectedShipment(s); setReceiverStep(1); }}
                    className="flex items-center justify-between card-hover"
                    style={{ padding: "14px 18px", borderRadius: 12, border: "1px solid var(--border-color)", background: "var(--surface)", cursor: "pointer", width: "100%", textAlign: "left" }}
                  >
                    <div>
                      <span className="mono" style={{ fontWeight: 600, color: "var(--primary)" }}>{s.tracking_code}</span>
                      <span style={{ fontSize: "0.8rem", color: "var(--text-muted)", marginLeft: 8 }}>{s.sender_name} → {s.recipient_name}</span>
                    </div>
                    <span className={`status-${s.status}`} style={{ padding: "3px 10px", borderRadius: 9999, fontSize: "0.7rem", fontWeight: 600 }}>
                      {STATUS_LABELS[s.status] || s.status}
                    </span>
                  </button>
                )) : (
                  <p style={{ color: "var(--text-muted)" }}>Nenhuma encomenda disponivel para simulacao</p>
                )}
              </div>
            </div>
          ) : (
            <>
              <StepIndicator steps={RECEIVER_STEPS} currentStep={receiverStep} onStepClick={id => setReceiverStep(id)} namespace="receiver_steps" />
              <div className="bento-item p-8">
                {receiverStep === 1 && <ReceiverStep1 selectedShipment={selectedShipment} />}
                {receiverStep === 2 && <ReceiverStep2 selectedShipment={selectedShipment} />}
                {receiverStep === 3 && <ReceiverStep3 selectedShipment={selectedShipment} />}
                {receiverStep === 4 && <ReceiverStep4 selectedShipment={selectedShipment} />}
                {receiverStep === 5 && <ReceiverStep5 selectedShipment={selectedShipment} />}
                <StepNavButtons
                  currentStep={receiverStep} totalSteps={5}
                  onPrev={() => { if (receiverStep === 1) setSelectedShipment(null); else setReceiverStep(receiverStep - 1); }}
                  onNext={() => setReceiverStep(Math.min(receiverStep + 1, 5))}
                />
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
